import * as React from "react";
import {
  Select,
  InputLabel,
  FormControl,
  createStyles,
  Theme,
  makeStyles,
  MenuItem,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Checkbox,
} from "@material-ui/core";
import SimpleTextField from "../../components/SimpleTextField";
import BaseFilter from "../../core/models/BaseFilter";
import EditIcon from "@material-ui/icons/Edit";
import Check from "@material-ui/icons/Check";
import Clear from "@material-ui/icons/Clear";
import { useHubx } from "../../HubX";
import {
  DO_UPDATE_ORGANIZATION,
  COUNT_ORGANIZATIONS,
  COUNT_DIRECTORIES,
  DO_UPDATE_DIRECTORIES,
} from "../../Fedx";
import { useSnackbar } from "notistack";
import { UpdateNameModel } from "../../core/contracts/UpdateNameModel";
import { get, truncate } from "lodash";
import { isArrayOfStrings, isArrayValid } from "./../../helpers/utility";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "width": 125,
      "background": theme.customPaper.background.cardBackgroundLightContrastWhite,
      "margin": theme.spacing(0, 0.5),

      "& legend": {
        maxWidth: 0,
      },

      "& .MuiOutlinedInput-input": {
        padding: theme.spacing(1, 4, 1, 1),
      },

      // replace default size of select
      "& .MuiInputLabel-outlined": {
        transform: "translate(14px, 10px) scale(1)",
      },

      // refixes transition animation on select
      "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
        transform: "translate(0px, -12px) scale(0.75)",
        background: "transparent",
        borderTop: "unset",
      },

      // change background color on hover of native select option
      "& option:hover": {
        backgroundColor: theme.palette.primary.main,
      },
    },

    editIcon: {
      color: "#717171",
      marginLeft: "10px",
      height: "2.5rem",
      width: "2.5rem",
    },

    menuItem: {
      justifyContent: "space-between",
    },

    buttonSecondary: {
      color: theme.customPalette.text.darkgrey,
      textDecoration: "underline",
    },
  })
);

type MaterialSelectProps = {
  filter: BaseFilter;
  canEdit?: boolean;
  hasCheckbox?: boolean;
  isDisabled?: boolean;
  classes?: Record<string, string>;
};

type MappedItem = {
  key: string | number;
  value: React.ReactNode;
  newValue: React.ReactNode;
  isEditing: boolean;
  confirmButtonPressed: boolean;
  hasChecked: boolean;
};

const DEFAULT_MAX_OPTIONS_ON_SCREEN = 2;

function MaterialSelect(props: MaterialSelectProps) {
  const { value, handleChange, items, id, label, variant, multiple = false, width } = props.filter;

  const [selectValues, setSelectValues] = React.useState<MappedItem[]>();
  const [openModal, setOpenModal] = React.useState(false);
  const [countInstallationsGroups, setCountInstallationsGroups] = React.useState<number>(0);
  const [updateItems, setUpdateItems] = React.useState(true);

  const canEdit = props.canEdit;
  const hasCheckbox = props.hasCheckbox;
  const isDisabled = props.isDisabled || false;

  let classes = useStyles();
  const hub = useHubx();
  const toastr = useSnackbar();

  if (props.classes) {
    classes = props.classes;
  }

  function handleClickEdit(event: React.ChangeEvent<unknown>, key: string) {
    preventClickEvent(event);

    const updatedSelectValues = selectValues?.map((x) => {
      if (x.key === key) x.isEditing = true;
      return x;
    });

    setSelectValues(updatedSelectValues);
  }

  function handleCheck(event: React.ChangeEvent<unknown>, key: string) {
    preventClickEvent(event);

    const updatedSelectValues = selectValues?.map((x) => {
      if (x.key === key) {
        x.hasChecked = !x.hasChecked;
      }
      return x;
    });

    setSelectValues(updatedSelectValues);
  }

  async function handleClickConfirm(event: React.ChangeEvent<unknown>, key: string) {
    preventClickEvent(event);

    let itemName = "";

    selectValues?.map((x) => {
      if (x.key === key) {
        x.confirmButtonPressed = true;
        itemName = x.value?.toString() ?? "";
      }
      return x;
    });

    try {
      if (id.toLowerCase() === "organizacao") {
        const APIResponse: number = await hub.request(COUNT_ORGANIZATIONS, itemName);
        setCountInstallationsGroups(APIResponse);
      } else if (id.toLowerCase() === "diretoria") {
        const APIResponse: number = await hub.request(COUNT_DIRECTORIES, itemName);
        setCountInstallationsGroups(APIResponse);
      } else {
        // eslint-disable-next-line no-console
        console.log(`An fetch method for this id was not found: ${id}`);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("MaterialSelect → handleClickConfirm -> error", error);
    } finally {
      setOpenModal(true);
    }
  }

  function handleModalClickConfirm(event: React.ChangeEvent<unknown>) {
    const menuItemToUpdate = selectValues?.filter((x) => x.confirmButtonPressed)[0];

    if (menuItemToUpdate?.value && menuItemToUpdate?.newValue) {
      const mappedMenuItem = {
        old_name: menuItemToUpdate?.value?.toString(),
        new_name: menuItemToUpdate?.newValue.toString(),
      };

      saveEditOnDatabase(mappedMenuItem);

      const updatedSelectValues = selectValues?.map((x) => {
        if (x.confirmButtonPressed) {
          x.value = x.newValue;
          x.isEditing = false;
          x.confirmButtonPressed = false;
        }
        return x;
      });

      setSelectValues(updatedSelectValues);
    }
    setOpenModal(false);
  }

  function handleClickCancel(event: React.ChangeEvent<unknown>, key: string) {
    preventClickEvent(event);

    const updatedSelectValues = selectValues?.map((x) => {
      if (x.key === key) x.isEditing = false;
      return x;
    });

    setSelectValues(updatedSelectValues);
  }

  function handleInputChange(event: React.ChangeEvent<{ value: string }>, key: string) {
    const updatedSelectValues = selectValues?.map((x) => {
      if (x.key === key) x.newValue = event.target.value;
      return x;
    });

    setSelectValues(updatedSelectValues);
  }

  const saveEditOnDatabase = async (item: UpdateNameModel) => {
    if (item) {
      try {
        const saveForm: UpdateNameModel = item as UpdateNameModel;

        if (id.toLowerCase() === "organizacao") {
          await hub.request(DO_UPDATE_ORGANIZATION, saveForm);
        } else if (id.toLowerCase() === "diretoria") {
          await hub.request(DO_UPDATE_DIRECTORIES, saveForm);
        } else {
          // eslint-disable-next-line no-console
          console.log(`An update method for this id was not found: ${id}`);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("MaterialSelect → handleClickConfirm -> error", error);

        toastr.enqueueSnackbar(
          `Ops! Ocorreu um erro ao tentar 
          "atualizar" ${item.old_name} para
          ${item.new_name}!`,
          { variant: "error" }
        );
      }
    }
  };

  function preventClickEvent(event: React.ChangeEvent<unknown>) {
    event.preventDefault();
    event.stopPropagation();
  }

  const toggleAllOnMultiselect = (values: unknown) => {
    if (typeof values == "object" && isArrayOfStrings(values)) {
      const valuesConverted = (values as unknown) as string[];
      if (valuesConverted.length && selectValues) {
        const newSelected = (selectValues.map((e: MappedItem) => {
          if (valuesConverted.includes(e.key as string)) {
            e.hasChecked = true;
          } else {
            e.hasChecked = false;
          }
          return e;
        }) as unknown) as MappedItem[];
        setSelectValues(newSelected);
      }
      if (valuesConverted.length > 1) {
        if (valuesConverted[0] != "all") {
          if (valuesConverted.includes("all")) {
            return ["all"];
          }
        } else {
          return valuesConverted.filter((e) => e != "all" && e != undefined);
        }
      }
    } else if (typeof values == "object" && !isArrayOfStrings(values)) {
      return ["all"];
    }
    return values;
  };

  const selectedValues: string[] = React.useMemo(() => {
    let newValues: string[] = [];
    if (multiple) {
      if (!isArrayValid(value)) {
        newValues = toggleAllOnMultiselect([value]) as string[];

        setSelectValues(
          selectValues?.map((e) => {
            if (isArrayValid(newValues) && newValues.includes(e.key as string)) {
              e.hasChecked = true;
            }
            return e;
          })
        );
        return newValues;
      }
    }
    newValues = toggleAllOnMultiselect(value) as string[];
    setSelectValues(
      selectValues?.map((e) => {
        if (isArrayValid(newValues) && newValues.includes(e.key as string)) {
          e.hasChecked = true;
        }
        return e;
      })
    );
    return newValues;
  }, [value]);

  const renderSelectedValues = (selected: any) => {
    if (multiple) {
      if (selected.length >= DEFAULT_MAX_OPTIONS_ON_SCREEN) {
        return `${selected.length} itens selecionados`;
      }
      return selected
        .filter((e: any) => e != undefined)
        .map((sec: string, key: number) => {
          const newObj = get(
            selectValues?.filter((x) => x.key === sec),
            "[0].value"
          );
          if (newObj) {
            return typeof newObj == "object"
              ? newObj != undefined
                ? newObj
                : ""
              : newObj + (key < selected.length - 1 ? "," : "");
          }
          return "";
        });
    }
    return get(
      selectValues?.filter((x) => x.key === selected),
      "[0].value"
    );
  };

  React.useEffect(() => {
    if (canEdit) {
      if (updateItems) {
        setSelectValues(
          items?.map((key) => {
            const selected = selectedValues && multiple ? selectedValues.find((e) => e == key.key) : undefined;
            return {
              key: key.key,
              value: key.value,
              newValue: key.value,
              isEditing: false,
              confirmButtonPressed: false,
              hasChecked: !!selected,
            };
          })
        );
      }
    } else {
      setSelectValues(
        items?.map((key) => {
          const selected = selectedValues && multiple ? selectedValues.find((e) => e == key.key) : undefined;
          return {
            key: key.key,
            value: key.value,
            newValue: key.value,
            isEditing: false,
            confirmButtonPressed: false,
            hasChecked: !!selected,
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <>
      <FormControl
        className={classes.root}
        style={width != 0 ? { width } : undefined}
        variant={variant || "outlined"}
      >
        <InputLabel id={`select-label-${id}-${label}`}>{label}</InputLabel>
        <Select
          labelId={`simple-select-label-${id}-${label}`}
          id={id}
          renderValue={renderSelectedValues}
          multiple={multiple}
          value={selectedValues}
          disabled={isDisabled}
          onChange={(e) => {
            setUpdateItems(false);
            if (handleChange) handleChange(e);
          }}
          label={label}
        >
          {selectValues &&
            selectValues.map(({ key, value, newValue, isEditing, hasChecked }) => {
              return (
                <MenuItem
                  key={key}
                  value={key}
                  className={classes.menuItem}
                  style={{ justifyContent: "start" }}
                  onClick={(e) => handleCheck(e, key.toString())}
                >
                  {key !== "new" && hasCheckbox && !isEditing && (
                    <Checkbox
                      checked={hasChecked}
                      // inputProps={{ "aria-label": "primary checkbox" }}
                    />
                  )}
                  {isEditing ? (
                    <SimpleTextField
                      onClick={(e) => preventClickEvent(e)}
                      value={newValue?.toString()}
                      handleChange={(e) => handleInputChange(e, key.toString())}
                    />
                  ) : (
                    value
                  )}

                  {key !== "new" && canEdit && !isEditing && (
                    <Tooltip title="Editar" className={classes.editIcon}>
                      <IconButton onClick={(e) => handleClickEdit(e, key.toString())}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {key !== "new" && canEdit && isEditing && (
                    <>
                      <Tooltip title="Confirmar" className={classes.editIcon}>
                        <IconButton onClick={(e) => handleClickConfirm(e, key.toString())}>
                          <Check />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar" className={classes.editIcon}>
                        <IconButton onClick={(e) => handleClickCancel(e, key.toString())}>
                          <Clear />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>

      <Dialog open={openModal}>
        <DialogTitle>
          A mudança irá afetar {countInstallationsGroups} grupo(s) de instalação, Deseja salvar as
          alterações?
        </DialogTitle>
        <DialogActions>
          <Button className={classes.buttonSecondary} onClick={() => setOpenModal(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={(e) => handleModalClickConfirm(e)}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MaterialSelect;
